import React, { useRef, useCallback, useState, useEffect } from 'react';
import { View, ScrollView, Dimensions, Platform, TouchableOpacity, StyleSheet, ImagePropTypes, TouchableWithoutFeedback, } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../../FoundationTestKeys';
import { useTheme } from '../../Themes';
import VFText from '../../VFText';

import styles from './WheelSelector.Styles';

const deviceWidth = Dimensions.get('window').width;

const WheelSelector = props => {
  const theme = useTheme();
  const sView = useRef();
  const momentumStarted = useRef(false);
  const isScrollTo = useRef(false);
  const dragStarted = useRef(false);
  const [timer, setTimer] = useState(0);
  const [itemHeight] = useState(props.itemHeight || 30);
  const [wrapperHeight] = useState(
    props.wrapperHeight || (props.style ? props.style.height : 0) || itemHeight * 5,
  );
  const [selectedIndex, setSelectedIndex] = useState(props.selectedIndex);

  useEffect(() => {
    if (props.selectedIndex) {
      setTimeout(() => {
        scrollToIndex(props.selectedIndex);
      }, 300);
    }

    return () => {
      timer && clearTimeout(timer);
    };
  }, []);

  const _renderPlaceHolder = useCallback(() => {
    const h = (wrapperHeight - itemHeight) / 2;
    const header = <View style={[styles.placeHolderViewStyle, { height: h }]} />;
    const footer = <View style={[styles.placeHolderViewStyle, { height: h }]} />;
    return {
      header,
      footer,
    };
  }, [wrapperHeight, itemHeight]);

  const { header, footer } = _renderPlaceHolder();

  const highlightWidth = (props.style ? props.style.width : 0) || deviceWidth;
  const wrapperStyle = {
    height: wrapperHeight,
    overflow: 'visible',
    flexDirection: 'row',
    alignItems: 'center',
  };
  const highlightStyle = {
    position: 'absolute',
    top: (wrapperHeight - itemHeight) / 2,
    height: itemHeight,
    width: highlightWidth,
  };

  const _scrollFix = useCallback(
    e => {
      let y = 0;
      const h = itemHeight;

      if (e.nativeEvent.contentOffset) {
        y = e.nativeEvent.contentOffset.y;
      }

      const currentSelectedIndex = Math.round(y / h);

      const _y = currentSelectedIndex * h;

      if (_y !== y) {
        if (Platform.OS === 'ios') {
          isScrollTo.current = true;
        }
        sView.current &&
          sView.current.scrollTo({
            y: _y,
          });
      }
      if (currentSelectedIndex === selectedIndex) {
        return;
      }
      if (props.onValueChange) {
        const selectedValue = props.dataSource[currentSelectedIndex];
        setSelectedIndex(currentSelectedIndex);
        props.onValueChange(selectedValue, currentSelectedIndex);
      }
    },
    [itemHeight, selectedIndex, props],
  );

  const _onScrollBeginDrag = useCallback(() => {
    dragStarted.current = true;
    if (Platform.OS === 'ios') {
      isScrollTo.current = false;
    }

    timer && clearTimeout(timer);
  }, [timer]);

  const _onScrollEndDrag = useCallback(
    e => {
      dragStarted.current = false;
      // if not used, event will be garbaged
      const _e = {
        nativeEvent: {
          contentOffset: {
            y: e.nativeEvent.contentOffset.y,
          },
        },
      };
      timer && clearTimeout(timer);
      setTimer(
        setTimeout(() => {
          if (!momentumStarted.current && !dragStarted.current) {
            _scrollFix(_e, 'timeout');
          }
        }, 10),
      );
    },
    [_scrollFix, timer],
  );

  const _onMomentumScrollBegin = useCallback(() => {
    momentumStarted.current = true;
    timer && clearTimeout(timer);
  }, [timer]);

  const _onMomentumScrollEnd = useCallback(
    e => {
      momentumStarted.current = false;
      // if (!isScrollTo.current && !dragStarted.current) {
      if (!isScrollTo.current && !momentumStarted.current && !dragStarted.current) {
        _scrollFix(e);
      }
    },
    [_scrollFix],
  );

  const scrollToIndex = useCallback(
    index => {
      setSelectedIndex(index);
      const y = itemHeight * index;
      sView.current &&
        sView.current.scrollTo({
          y: y,
          animated: true,
        });
    },
    [itemHeight],
  );

  const _renderItem = useCallback(
    (data, index) => {
      // const isSelected = index === this.state.selectedIndex
      let item;
      if (props.renderItem) {
        item = props.renderItem(
          data,
          index,
          selectedIndex,
          props.singleValue,
          props.giftIcon,
        );
      } else {
        // provide base implementation if render props is not passed
        item = <VFText style={styles.itemText(theme)} localize={false} i18nKey={data} />;
      }

      return (
        <TouchableWithoutFeedback
          onPress={() => {
            setSelectedIndex(index)
            props.onValueChange(props.dataSource[index], index)
          }}
        >
          <View
            style={[styles.itemWrapper, { height: itemHeight }]}
            key={index}
          >
            {item}
          </View>
        </TouchableWithoutFeedback>
      );
    },
    [itemHeight, props, selectedIndex],
  );

  const _renderCurrencyWithValue = () => (
    <View style={styles.currencyWrapper(props.singleValue, props.isCurrencyFirst)}>
      {props.currency && (
        <VFText
          style={styles.itemCurrency(theme)}
          accessibilityLabel={props.currencyAccessibilityLabel}
          localize={false}
          i18nKey={props.currency}
        />
      )}
    </View>
  );

  return (
    <View style={wrapperStyle}>
      <View style={highlightStyle} />
      {props.isCurrencyFirst && _renderCurrencyWithValue()}
      <ScrollView
        style={{ height: '100%' }}
        ref={sViewRef => {
          sView.current = sViewRef;
        }}
        bounces={false}
        _getItemStyle
        scrollEnabled={!props.singleValue}
        showsVerticalScrollIndicator={false}
        onMomentumScrollBegin={_onMomentumScrollBegin}
        onMomentumScrollEnd={_onMomentumScrollEnd}
        onScrollBeginDrag={_onScrollBeginDrag}
        onScrollEndDrag={_onScrollEndDrag}
        nestedScrollEnabled
        testID={FoundationTestKeys.wheelSelector}
      >
        {header}
        {props.dataSource.map(_renderItem.bind(this))}
        {footer}
        {Platform.OS !== 'web' && (
          <TouchableOpacity style={StyleSheet.absoluteFillObject} />
        )}
      </ScrollView>
      {!props.isCurrencyFirst && _renderCurrencyWithValue()}
    </View>
  );
};

WheelSelector.propTypes = {
  style: ViewPropTypes.style,
  dataSource: PropTypes.array.isRequired,
  selectedIndex: PropTypes.number,
  onValueChange: PropTypes.func,
  renderItem: PropTypes.func,
  itemHeight: PropTypes.number,
  wrapperHeight: PropTypes.number,
  currency: PropTypes.string,
  currencyAccessibilityLabel: PropTypes.string,
  singleValue: PropTypes.bool,
  giftIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isCurrencyFirst: PropTypes.bool,
};

export default WheelSelector;
